import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CPagination,
  CRow,
  CLabel,
  CInput,
  CInputGroupText,
  CInputGroup,
} from "@coreui/react";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { BsFillPlusCircleFill } from "react-icons/bs";
import { useHistory } from "react-router-dom";

import { getRoutePath } from "../../routes";
import { successAlert, warningAlert } from "../utils/messages";
import { SUCCESS } from "../../utils/constants/tags";

import { getList } from "../../api/generics";
import Country from "../../models/country";
import CountryUpdateModal from "./CountryUpdateModal";
import CountryDeleteModal from "./CountryDeleteModal";

const ITEMS_PER_PAGE = 10;

const CountryList = () => {
  const fields = [
    { key: "name", _classes: ["text-center"], label: "Nombre" },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];
  const isSuperUser = useSelector((state: RootState) => state.user.isSuperUser);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [country, setCountry] = useState<Country | undefined>(undefined);
  const [search, setSearch] = useState<string>("");

  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState<Country[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const history = useHistory();

  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }
    let search = urlParams.get("search") ? urlParams.get("search") : "";
    let map = new Map();
    map.set("search", search);
    map.set("page", page);
    return map;
  };

  const fetchCountries = async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.get("page") - 1);
    const countriesStatus = await getList<Country>(
      "/countries/",
      limit,
      offset,
      urlParams
    );
    if (countriesStatus.status === SUCCESS) {
      if (countriesStatus.data !== undefined) {
        const count = countriesStatus.data.count;
        const pages = Math.ceil((count ? count : 0) / ITEMS_PER_PAGE);
        setTotalPages(pages);
        setCountries(countriesStatus.data.items);
        setCurrentPage(urlParams.get("page"));
      }
    } else {
      const message = countriesStatus.detail
        ? countriesStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
  };

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    let previousParams = getURLParams();
    urlParams.set(
      "search",
      previousParams.get("search") ? previousParams.get("search") : ""
    );
    urlParams.set("page", page.toString());
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const onAddClick = () => {
    setShowEditModal(true);
  };

  const onUpdateClick = (country: Country) => {
    setCountry(country);
    setShowEditModal(true);
  };

  const onDeleteClick = (country: Country) => {
    setCountry(country);
    setShowDeleteModal(true);
  };

  const onEditCancel = () => {
    setCountry(undefined);
    setShowEditModal(false);
  };

  const onEditSuccess = () => {
    successAlert("Datos guardados con éxito!");
    setShowEditModal(false);
    setCountries([]);
    setLoading(true);
    fetchCountries();
  };

  const onDeleteCancel = () => {
    setCountry(undefined);
    setShowDeleteModal(false);
  };

  const onDeleteSuccess = () => {
    successAlert("Registro eliminado con éxito!");
    setShowDeleteModal(false);
    setCountries([]);
    setLoading(true);
    fetchCountries();
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onSearch = () => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("search", search);
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      onSearch();
    }
  };

  useEffect(() => {
    fetchCountries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      if (location.pathname !== getRoutePath("countries")) {
        return;
      }
      fetchCountries();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2 ">
                <h3>Paises</h3>
              </div>
              <div className="p-2 ">
                <CButton
                  color="primary"
                  className="float-right"
                  onClick={onAddClick}
                >
                  <BsFillPlusCircleFill />
                  &nbsp; Agregar Nuevo
                </CButton>
              </div>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol>
                  <CLabel
                    className="visually-hidden"
                    htmlFor="autoSizingInputGroup"
                  ></CLabel>
                  <CInputGroup>
                    <CInputGroupText>
                      <i className="fa fa-search"></i>
                    </CInputGroupText>
                    <CInput
                      type="text"
                      placeholder="Introduzca el termino de busqueda"
                      onChange={onSearchChange}
                      value={search}
                      onKeyDown={handleKeyDown}
                    />
                    <CButton
                      type="button"
                      color="primary"
                      variant="outline"
                      onClick={onSearch}
                    >
                      Filtrar
                    </CButton>
                  </CInputGroup>
                </CCol>
              </CRow>
              <div>
                <CDataTable
                  noItemsView={<h2 className="text-center">Sin Resultados</h2>}
                  addTableClasses={"table-fixed"}
                  fields={fields}
                  items={countries}
                  striped
                  border
                  loading={loading}
                  responsive
                  scopedSlots={{
                    
                    actions: (item: Country) => {
                      const editButton = (
                        <CButton
                          className="text-white"
                          color="warning"
                          onClick={() => {
                            onUpdateClick(item);
                          }}
                        >
                          <i className="fa fa-pencil"></i>
                        </CButton>
                      );
                      const deleteButton = (
                        <CButton
                          className="text-white"
                          color="danger"
                          onClick={() => {
                            onDeleteClick(item);
                          }}
                        >
                          <i className="fa fa-trash"></i>
                        </CButton>
                      );
                      
                      return (
                        <td className="text-right">
                          <CButtonGroup>
                            
                            {isSuperUser ? (
                              <>
                                
                                {editButton} {deleteButton}
                              </>
                            ) : (
                              <></>
                            )}
                          </CButtonGroup>
                        </td>
                      );
                    },
                  }}
                />
                <div className="d-flex justify-content-center">
                  <CPagination
                    pages={totalPages}
                    activePage={currentPage}
                    onActivePageChange={(i: number) => onPageChange(i)}
                    className={totalPages < 2 ? "d-none" : ""}
                  />
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <CountryUpdateModal
        show={showEditModal}
        onCancel={onEditCancel}
        onSuccess={onEditSuccess}
        country={country}
      />
      <CountryDeleteModal
        show={showDeleteModal}
        onCancel={onDeleteCancel}
        onSuccess={onDeleteSuccess}
        country={country}
      />
      
    </>
  );
};

export default CountryList;
